import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { buildSharePayload, getInstagramShareCaption, getShareLinks } from '../../utils/shareUtils';
import './ContentShareMenu.css';

/**
 * props:
 * - title
 * - text
 * - shareHash
 * - label
 * - variant: 'compact' | 'default'
 */
function ContentShareMenu({ title, text, shareHash, label = 'Share', variant = 'default' }) {
    const [open, setOpen] = useState(false);
    const [copyState, setCopyState] = useState('idle');
    const [actionFeedback, setActionFeedback] = useState('');
    const rootRef = useRef(null);

    const shareLinks = getShareLinks({ title, text, shareHash });
    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        if (!open) return undefined;

        function handlePointerDown(event) {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        }

        function handleEscape(event) {
            if (event.key === 'Escape') setOpen(false);
        }

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open]);

    async function handleNativeShare() {
        const { url, message, title: shareTitle } = buildSharePayload({ title, text, shareHash });

        try {
            await navigator.share({
                title: shareTitle,
                text: message,
                url,
            });
            setOpen(false);
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.error(err);
            }
        }
    }

    async function handleCopyLink() {
        const { url } = buildSharePayload({ title, text, shareHash });

        try {
            await navigator.clipboard.writeText(url);
            setCopyState('copied');
            window.setTimeout(() => setCopyState('idle'), 2000);
        } catch (err) {
            console.error(err);
            setCopyState('error');
            window.setTimeout(() => setCopyState('idle'), 2000);
        }
    }

    function handlePlatformClick() {
        setOpen(false);
        setActionFeedback('');
    }

    async function handleInstagramShare() {
        const caption = getInstagramShareCaption({ title, text, shareHash });

        try {
            await navigator.clipboard.writeText(caption);
            setActionFeedback('Caption copied for Instagram');
            window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
            window.setTimeout(() => {
                setOpen(false);
                setActionFeedback('');
            }, 1800);
        } catch (err) {
            console.error(err);
            setActionFeedback('Could not copy caption');
            window.setTimeout(() => setActionFeedback(''), 2000);
        }
    }

    function handleShareAction(action) {
        if (action === 'instagram') {
            handleInstagramShare();
            return;
        }
        handlePlatformClick();
    }

    return (
        <div
            ref={rootRef}
            className={`content-share${variant === 'compact' ? ' content-share--compact' : ''}`}
        >
            <button
                type="button"
                className="content-share-trigger"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((prev) => !prev)}
            >
                <Icon icon="mdi:share-variant-outline" aria-hidden="true" />
                <span>{label}</span>
            </button>

            {open ? (
                <div className="content-share-menu" role="menu" aria-label={`${label} options`}>
                    {canNativeShare ? (
                        <button
                            type="button"
                            className="content-share-menu-item content-share-menu-item--native"
                            role="menuitem"
                            onClick={handleNativeShare}
                        >
                            <Icon icon="mdi:share-outline" aria-hidden="true" />
                            <span>Share via device</span>
                        </button>
                    ) : null}

                    <div className="content-share-menu-grid">
                        {shareLinks.map((link) => (
                            link.action ? (
                                <button
                                    key={link.id}
                                    type="button"
                                    className="content-share-platform"
                                    role="menuitem"
                                    aria-label={link.label}
                                    title={link.label}
                                    onClick={() => handleShareAction(link.action)}
                                >
                                    <Icon icon={link.icon} aria-hidden="true" />
                                </button>
                            ) : (
                                <a
                                    key={link.id}
                                    href={link.href}
                                    className="content-share-platform"
                                    role="menuitem"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    title={link.label}
                                    onClick={handlePlatformClick}
                                >
                                    <Icon icon={link.icon} aria-hidden="true" />
                                </a>
                            )
                        ))}
                    </div>

                    {actionFeedback ? (
                        <p className="content-share-feedback" role="status">
                            {actionFeedback}
                        </p>
                    ) : null}

                    <button
                        type="button"
                        className="content-share-copy"
                        role="menuitem"
                        onClick={handleCopyLink}
                    >
                        <Icon icon="mdi:link-variant" aria-hidden="true" />
                        <span>
                            {copyState === 'copied'
                                ? 'Link copied'
                                : copyState === 'error'
                                    ? 'Could not copy link'
                                    : 'Copy link'}
                        </span>
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export default ContentShareMenu;